<template>
  <modal :show="show" @close="close">
    <v-flex slot="title">{{ id ? 'Edycja' : 'Dodaj grupę' }}</v-flex>

    <div>
      <v-layout mt-3 row wrap justify-center>
        <v-flex xs11>
          <w-schema :model="$schema.name">
            <v-text-field label="Nazwa grupy *" counter clearable></v-text-field>
          </w-schema>
        </v-flex>
      </v-layout>

      <v-layout row justify-center>
        <v-flex xs11>
          <w-schema :model="$schema.auto">
            <v-radio-group label="Rodzaj grupy:" @change="resetData">
              <v-radio label="Manualna" value="false"></v-radio>
              <v-radio label="Automatyczna" value="true"></v-radio>
            </v-radio-group>
          </w-schema>
        </v-flex>
      </v-layout>

      <v-layout v-if="$schema.auto.$value == 'true'" row wrap justify-center>
        <v-flex xs11>
          <v-layout row wrap>
            <v-flex xs12>
              <w-schema :model="$schema.data.field">
                <v-select
                  item-text="text"
                  item-value="value"
                  label="Kryterium"
                  :items="fieldOptions"
                  @change="setSelected"
                ></v-select>
              </w-schema>
            </v-flex>

            <v-flex v-if="$schema.data.field.$value" xs12>
              <w-select
                v-if="selected.inputType === InputType.SELECT"
                item-text="text"
                item-value="value"
                :model="$schema.data.number"
                :label="selected.text + ' *'"
                :items="selected.options"
              ></w-select>

              <v-layout v-else-if="selected.inputType === InputType.RANGE">
                <v-flex xs5>
                  <w-schema :model="$schema.data.range.min">
                    <v-text-field label="Od *" class="mt-0" hide-details type="number"></v-text-field>
                  </w-schema>
                </v-flex>

                <v-spacer xs2></v-spacer>

                <v-flex xs5>
                  <w-schema :model="$schema.data.range.max">
                    <v-text-field label="Do *" class="mt-0" hide-details type="number"></v-text-field>
                  </w-schema>
                </v-flex>
              </v-layout>

              <v-flex v-else-if="selected.inputType === InputType.TERC_PICKER && show" xs12>
                <terc-picker :model="$schema.data.data"></terc-picker>
              </v-flex>

              <v-layout v-else-if="selected.inputType === InputType.SIMC_PICKER && show">
                <v-flex xs12>
                  <div>
                    <terc-picker :model="$schema.data.data"></terc-picker>

                    <simc-picker-multiple
                      field="array"
                      :model="$schema.data"
                      :terc="$schema.data.data.terc.$value"
                    ></simc-picker-multiple>
                  </div>
                </v-flex>
              </v-layout>

              <v-layout v-else-if="selected.inputType === InputType.ULIC_PICKER && show">
                <v-flex xs12 class="text-xs-left">
                  <terc-picker :model="$schema.data.data"></terc-picker>

                  <simc-picker :model="$schema.data.data" :terc="$schema.data.data.terc.$value"></simc-picker>

                  <div v-if="$schema.data.data.simc.$value">
                    <ulic-picker-multiple
                      :model="$schema.data.data"
                      :terc="$schema.data.data.terc.$value"
                      :simc="$schema.data.data.simc.$value"
                      @saveUlic="saveUlic"
                    >
                      <div slot="errors">{{error}}</div>

                      <div v-if="streets.length > 0">Lista dodanych ulic: </div>
                    </ulic-picker-multiple>

                    <table>
                      <tr v-for="(street, index) in streets" :key="index">
                        <td class="main-table-cell">{{ street.val }} {{ street.name }}</td>

                        <td>
                          <v-btn icon flat color="error" @click="deleteUlicPicker(index)">
                            <v-icon>mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </table>
                  </div>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </div>

    <v-layout slot="actions">
      <v-spacer></v-spacer>

      <v-btn color="error" flat @click="close">Anuluj</v-btn>

      <v-btn color="success" flat @click="beforeSave">Zapisz</v-btn>
    </v-layout>
  </modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapState } from 'vuex';
import { WSchema, WSelect } from '@witkac/vuetify-components';
import { Gender, GenderTranslation, GroupDataTypes, InputType } from '@witkac/kartamiejska-shared';

import TercPicker from '@/components/forms/TERCPicker';
import SimcPicker from '@/components/forms/SIMCPicker';
import SimcPickerMultiple from '@/components/forms/SIMCPickerMultiple';
import UlicPickerMultiple from '@/components/forms/ULICPickerMultiple';
import Modal from '@/components/material/Modal';
import { manageMixin } from '@/mixins/manage';
import schema from '@/schemas/group.schema';

const emptyData = () => ({
  name: '',
  auto: false,
  data: {
    type: null,
    field: '',
    number: null,
    text: '',
    range: {
      min: null,
      max: null
    },
    array: [],
    data: {
      terc: '',
      simc: '',
      ulic: '',
      woj: '',
      pow: '',
      gmi: '',
      city: '',
      streets: ''
    }
  }
});

// @vuese
// Modal dodawania grupy
// @group groups
export default {
  mixins: [manageMixin('groups', {}, emptyData)],
  components: { Modal, WSchema, WSelect, TercPicker, SimcPicker, SimcPickerMultiple, UlicPickerMultiple },
  schema,
  data() {
    return {
      InputType,
      fieldOptions: [
        {
          text: 'Płeć',
          value: 'gender',
          inputType: InputType.SELECT,
          type: GroupDataTypes.Number,
          options: [
            {
              text: GenderTranslation[Gender.FEMALE],
              value: Gender.FEMALE
            },
            {
              text: GenderTranslation[Gender.MALE],
              value: Gender.MALE
            }
          ]
        },
        {
          text: 'Wiek',
          value: 'age',
          inputType: InputType.RANGE,
          type: GroupDataTypes.Age
        },
        {
          text: 'Gmina',
          value: 'terc',
          inputType: InputType.TERC_PICKER,
          type: GroupDataTypes.StringExact
        },
        {
          text: 'Miejscowość',
          value: 'simc',
          inputType: InputType.SIMC_PICKER,
          type: GroupDataTypes.ArrayIncludes
        },
        {
          text: 'Ulica',
          value: 'ulic',
          inputType: InputType.ULIC_PICKER,
          type: GroupDataTypes.ArrayIncludes
        }
      ],
      streets: [],
      error: '',
      validation: false
    };
  },
  methods: {
    // @vuese
    // Zwraca tablicę z nazwami wymaganych pól z dodatkowych danych
    requiredFields(type) {
      const required = {
        ulic: ['simc', 'city', 'gmi', 'pow', 'woj'],
        simc: ['gmi', 'pow', 'woj'],
        terc: ['gmi', 'pow', 'woj']
      };

      return required[type] ? required[type] : [];
    },
    // @vuese
    // Ustawia które pola z dodatkowych danych są wymagane
    setRequired(value) {
      const allFields = ['woj', 'pow', 'gmi', 'city', 'simc', 'ulic'];
      const requiredFields = this.requiredFields(value);

      allFields.forEach(
        field => (this.$schema.data.data[field].$schema.fieldRequired = requiredFields.includes(field))
      );
    },
    // @vuese
    // Ustawia typ wartości dla wybranego kryterium grupy automatycznej
    setSelected(value) {
      this.$schema.data.type.$model = this.selected.type;
      this.resetDataValue();
      this.setRequired(value);
    },
    // @vuese
    // Czyści wszystkie dane grupy automatycznej
    resetData() {
      this.$schema.$setData({ data: { type: null, field: '' } });
      this.resetDataValue();
    },
    // @vuese
    // Czyści pola wartości grupy automatycznej
    resetDataValue() {
      this.streets = [];
      this.$schema.$setData(
        {
          data: {
            number: null,
            text: '',
            array: [],
            range: { min: null, max: null },
            data: { terc: '', simc: '', ulic: '', woj: '', pow: '', gmi: '', city: '', streets: '' }
          }
        },
        undefined,
        true
      );
    },
    // @vuese
    // Aktualizuje scheme i przechodzi do walidacji i zapisu modelu
    beforeSave() {
      this.setTercInSchema();
      this.updateUlicInSchema();
      this.save();
    },
    // @vuese
    // dodaje grupę
    add() {
      // Emituje event do zapisu danych
      // @arg Zwalidowany model do utworzenia w bazie
      this.$emit('save', this.groupData());
    },
    // @vuese
    // aktualizuje grupę
    update() {
      // Emituje event do edytowania danych
      // @arg Zwalidowany model do edycji w bazie
      this.$emit('update', { id: this.id, ...this.groupData() });
    },
    // @vuese
    // zwraca dane grupy zależne od parametru auto
    groupData() {
      const { auto, name, data } = this.model;
      return { name, auto, data: auto ? data : null };
    },
    // @vuese
    // aktualizuje dane ulic w schemie
    updateUlicInSchema() {
      if (this.$schema.data.field.$value === 'ulic') {
        if (this.streets.length > 0) {
          this.$schema.$setData(
            {
              data: {
                array: this.streets.map(item => item.ulic),
                data: {
                  streets: this.streets.map(item => item.name).join()
                }
              }
            },
            undefined,
            true
          );
        } else {
          this.$schema.$setData(
            {
              data: {
                array: [],
                data: {
                  streets: ''
                }
              }
            },
            undefined,
            true
          );
        }
      }
    },
    // @vuese
    // Usuwa wybraną ulicę z listy
    // @arg `index` - indeks usuwanej ulicy
    deleteUlicPicker(index) {
      this.streets.splice(index, 1);
    },
    // @vuese
    // Dodaje wybraną ulicę do listy
    // @arg `item` - obiekt z wartością ULIC oraz nazwą ulicy
    saveUlic(item) {
      this.streets.push(item);
    },
    // @vuese
    // Zapisuje wartość pola terc w schemie
    setTercInSchema() {
      if (this.$schema.data.field.$value === 'terc') {
        this.$schema.data.$setData({
          text: this.$schema.data.data.terc.$value
        });
      }
    }
  },
  watch: {
    show() {
      this.setRequired(this.$schema.data.field.$value);
      if (this.$schema.data.field.$value === 'ulic') {
        const symbols = this.$schema.data.array.$value;
        const streets = this.$schema.data.data.streets.$value.split(',');
        this.streets = symbols.map((ulic, index) => ({ ulic, name: streets[index] }));
      } else {
        this.streets = [];
      }
    },
    streets(value) {
      if (value.length > 0) {
        this.error = '';
      } else {
        this.error = 'Należy dodać co najmniej jedną ulicę';
      }
    }
  },
  computed: {
    selected() {
      const field = this.$schema.data.field.$model;
      if (field) {
        return this.fieldOptions.find(option => option.value === field);
      }
    }
  }
};
</script>

<style scoped>
table {
  width: 100%;
  table-layout: fixed;
  margin-top: 0.9em;
}
.main-table-cell {
  width: 90%;
  white-space: normal;
}
</style>
